import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { MessageCircle, Gamepad2, Network } from "lucide-react";
import analytics from "../../../../libs/analytics";

const HowItWorks = () => {
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const features = [
    {
      icon: MessageCircle,
      title: "CONVERSADOR GUIADO",
      description: "Prática por voz com feedback estratégico para construir respostas com segurança.",
      gradient: "from-purple-600 to-pink-600",
    },
    {
      icon: Gamepad2,
      title: "ATARIC LAND",
      description: "Ambiente imersivo onde você pratica inglês em contexto real, jogando, interagindo e se divertindo.",
      gradient: "from-cyan-600 to-blue-600",
    },
    {
      icon: Network,
      title: "ENGLISH MIND MAPS",
      description: "Estrutura mental organizada para você falar com clareza e segurança.",
      gradient: "from-amber-600 to-orange-600",
    },
  ];

  return (
    <section id="como-funciona" className="py-20 md:py-32 bg-card relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Como o ecossistema Ataric{" "}
            <span className="text-gradient glow-text">funciona na prática</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Você não entra em uma conversa aleatória. Você entra em um sistema estruturado para desenvolver habilidades reais.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              className="group"
            >
              <div className="card-glass rounded-2xl p-8 h-full hover:border-primary/50 transition-all duration-300 hover:shadow-[0_0_40px_hsl(277_78%_40%/0.3)]">
                <div className={`w-20 h-20 rounded-2xl mb-6 flex items-center justify-center bg-gradient-to-br ${feature.gradient} shadow-lg`}>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ 
                      duration: 2, 
                      repeat: Infinity, 
                      ease: "easeInOut" 
                    }}
                  >
                    <feature.icon className="w-10 h-10 text-white" />
                  </motion.div>
                </div>
                <h3 className="font-display text-xl font-bold mb-4 text-foreground">
                  <motion.span
                    animate={{ 
                      textShadow: [
                        "0 0 10px hsl(277 78% 40% / 0.3)",
                        "0 0 20px hsl(277 78% 40% / 0.5)",
                        "0 0 10px hsl(277 78% 40% / 0.3)"
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    {feature.title}
                  </motion.span>
                </h3>
                <p className="text-foreground/80 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Button type="button" variant="cta" size="xl" onClick={(e) => { analytics.landingCtaClicked('how_it_works'); scrollToSection(e, 'depoimentos'); }}>
            👉 Entrar no ecossistema
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
